import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAppSettings } from "../services/appSettingsService";
import { useActiveFamily } from "./ActiveFamilyContext";
import { useAuth } from "./AuthContext";

export type Preferences = {
  kidsMode: boolean;
  soundMode: boolean;
  scoreUnit: "stars" | "coins";
  showAnalytics: boolean;
  showDashboardActivableFeatures: boolean;
  setKidsMode: (v: boolean) => void;
  setSoundMode: (v: boolean) => void;
  setScoreUnit: (v: "stars" | "coins") => void;
  setShowAnalytics: (v: boolean) => void;
  setShowDashboardActivableFeatures: (v: boolean) => void;
};

const PreferencesContext = createContext<Preferences | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const activeFamilyId = activeFamily?.id || null;

  const getFamilyDefaultScoreUnit = (): "stars" | "coins" => {
    const raw = (activeFamily as any)?.scoreUnit || (activeFamily as any)?.unit;
    return raw === "coins" ? "coins" : "stars";
  };

  // Forzado a false temporalmente para descontinuar el Modo Kids
  const [kidsMode, setKidsModeState] = useState<boolean>(false);
  const [soundMode, setSoundModeState] = useState<boolean>(
    () => typeof window !== "undefined" && localStorage.getItem("sound_mode") === "1"
  );
  const [scoreUnit, setScoreUnitState] = useState<"stars" | "coins">(
    () => {
      if (typeof window === "undefined") return "stars";
      const scopedKey = activeFamilyId ? `score_unit_${activeFamilyId}` : null;
      // Con familia activa: nunca leer el legacy global porque contamina otros grupos
      const raw = scopedKey ? localStorage.getItem(scopedKey) : localStorage.getItem("score_unit");
      if (raw === "coins") return "coins";
      if (raw === "stars") return "stars";
      // Migración: si existía 'points', convertir a 'stars'
      if (raw === "points") {
        try {
          if (scopedKey) localStorage.setItem(scopedKey, "stars");
          else localStorage.setItem("score_unit", "stars");
        } catch {}
        return "stars";
      }
      // Si hay familia activa y no hay valor guardado, usar el valor del doc de la familia
      if (scopedKey) return getFamilyDefaultScoreUnit();
      return "stars";
    }
  );
  const [showAnalytics, setShowAnalyticsState] = useState<boolean>(true); // Default mientras carga
  const [showDashboardActivableFeatures, setShowDashboardActivableFeaturesState] = useState<boolean>(true);

  const setShowDashboardActivableFeatures = (v: boolean) => {
    setShowDashboardActivableFeaturesState(v);
    const uid = user?.uid || null;
    if (!uid) return;
    try {
      localStorage.setItem(`dashboard_activable_features_${uid}`, v ? "1" : "0");
    } catch {}
    try {
      window.dispatchEvent(new Event("prefs-update"));
    } catch {}
  };

  // Fuente de verdad por grupo: si hay familia activa, sincronizar desde Firestore (activeFamily)
  useEffect(() => {
    if (!activeFamilyId) return;
    const v = getFamilyDefaultScoreUnit();
    setScoreUnitState(v);
    try {
      localStorage.setItem(`score_unit_${activeFamilyId}`, v);
    } catch {}
  }, [activeFamilyId, (activeFamily as any)?.scoreUnit, (activeFamily as any)?.unit]);

  // Hydrate from localStorage (con migración de 'points' -> 'stars')
  useEffect(() => {
    setKidsModeState(false);
    try {
      localStorage.removeItem("kids_mode");
    } catch {}
    setSoundModeState(localStorage.getItem("sound_mode") === "1");
    const scopedKey = activeFamilyId ? `score_unit_${activeFamilyId}` : null;
    const su = scopedKey ? localStorage.getItem(scopedKey) : localStorage.getItem("score_unit");
    if (su === "coins" || su === "stars") setScoreUnitState(su as any);
    else if (su === "points") { // migrar
      try {
        if (scopedKey) localStorage.setItem(scopedKey, "stars");
        else localStorage.setItem("score_unit", "stars");
      } catch {}
      setScoreUnitState("stars");
    } else if (scopedKey) {
      // Si no hay valor scoped, inicializarlo desde el doc de la familia (Firestore via ActiveFamilyContext)
      const v = getFamilyDefaultScoreUnit();
      setScoreUnitState(v);
      try {
        localStorage.setItem(scopedKey, v);
      } catch {}
    }
  }, [activeFamilyId]);

  // Re-hidratar scoreUnit cuando otra parte de la app actualiza localStorage
  // (por ejemplo, al guardar cambios en el grupo desde FamiliasForm)
  useEffect(() => {
    const rehydrateScoreUnit = () => {
      try {
        const scopedKey = activeFamilyId ? `score_unit_${activeFamilyId}` : null;
        const su = scopedKey ? localStorage.getItem(scopedKey) : localStorage.getItem("score_unit");
        if (su === "coins" || su === "stars") setScoreUnitState(su as any);
      } catch {}
    };

    const handler = () => rehydrateScoreUnit();
    window.addEventListener('prefs-update', handler);
    // También cubrir cambios de storage entre tabs
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('prefs-update', handler);
      window.removeEventListener('storage', handler);
    };
  }, [activeFamilyId]);

  // Cargar showAnalytics desde Firestore (admin settings)
  useEffect(() => {
    const loadAnalyticsSetting = async () => {
      try {
        const settings = await getAppSettings();
        setShowAnalyticsState(settings.showAnalytics);
      } catch (error) {
        console.error('[PreferencesContext] Error loading showAnalytics, using default (true):', error);
        // En caso de error, mantener valor por defecto (true)
      }
    };
    loadAnalyticsSetting();
  }, []);

  useEffect(() => {
    const uid = user?.uid || null;
    if (!uid) {
      setShowDashboardActivableFeaturesState(true);
      return;
    }
    try {
      const raw = localStorage.getItem(`dashboard_activable_features_${uid}`);
      if (raw === "0") setShowDashboardActivableFeaturesState(false);
      else if (raw === "1") setShowDashboardActivableFeaturesState(true);
      else setShowDashboardActivableFeaturesState(true);
    } catch {
      setShowDashboardActivableFeaturesState(true);
    }
  }, [user?.uid]);

  const setKidsMode = (v: boolean) => {
    setKidsModeState(v);
    if (v) localStorage.setItem("kids_mode", "1");
    else localStorage.removeItem("kids_mode");
    try {
      window.dispatchEvent(new Event("prefs-update"));
    } catch {}
  };

  const setSoundMode = (v: boolean) => {
    setSoundModeState(v);
    if (v) localStorage.setItem("sound_mode", "1");
    else localStorage.removeItem("sound_mode");
    try {
      window.dispatchEvent(new Event("prefs-update"));
    } catch {}
  };

  const setScoreUnit = (v: "stars" | "coins") => {
    setScoreUnitState(v);
    try {
      const scopedKey = activeFamilyId ? `score_unit_${activeFamilyId}` : null;
      if (scopedKey) localStorage.setItem(scopedKey, v);
      else localStorage.setItem("score_unit", v);
    } catch {}
    try {
      window.dispatchEvent(new Event("prefs-update"));
    } catch {}
  };

  // showAnalytics ahora es read-only desde Firestore (controlado por admin)
  const setShowAnalytics = (v: boolean) => {
    // No-op: esta configuración ahora se controla desde Admin Settings
    console.warn('showAnalytics es controlado por Admin Settings. Use el panel de administración para cambiarlo.');
  };

  // Reflect classes globally for CSS-wide effects
  useEffect(() => {
    const root = document.documentElement;
    if (kidsMode) root.classList.add("kids-mode");
    else root.classList.remove("kids-mode");
  }, [kidsMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (soundMode) root.classList.add("sound-on");
    else root.classList.remove("sound-on");
  }, [soundMode]);

  const value = useMemo<Preferences>(
    () => ({
      kidsMode,
      soundMode,
      scoreUnit,
      showAnalytics,
      showDashboardActivableFeatures,
      setKidsMode,
      setSoundMode,
      setScoreUnit,
      setShowAnalytics,
      setShowDashboardActivableFeatures,
    }),
    [kidsMode, soundMode, scoreUnit, showAnalytics, showDashboardActivableFeatures]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): Preferences => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used within a PreferencesProvider");
  return ctx;
};
